import type { PolygonWithHoles, Ring } from '../../geo/types';
import { ringBounds } from '../../geo/polygon';
import { eachRunIn, gridOver, type ClaimGrid } from './claimGrid';

/**
 * PLAN Lots: the ground of the walled blocks no plot reaches, the slivers between plots, the wedges
 * where plots fan out on a bend and the patches at corners and behind the plots' back lines, is given
 * to the houses around it, each cell to the house whose ground lies nearest along the free ground:
 * the woodcut of 1544 draws no strip or wedge between or behind the fronts, and the painting paints
 * that ground built. `cell` is the claims' grain. A building on claimed ground is drawn over cells
 * `least` wide or more each way; its cells are drawn in by `inset`, which clears the lots they were
 * sampled beside, then run out again to meet them, so it stands at least `least` less twice the inset
 * wide and deep: 1.5 m, a privy's room over a cesspit of 1.5 to 2 m (AAR 60), and no smaller shed
 * that would stand among the roofs as a box. Free ground that holds a square
 * `field` metres wide clear of every house, plot, street and wall is no leftover of the plots but open
 * ground in its own right, a field or a bleaching ground the plots never reached, and stays open.
 * So does free ground within `street` metres of a street, square or water, measured along the free
 * ground: a house's side wall on a steeg keeps its windows and its shop, and a front stays a front.
 */
export const CLAIM = { cell: 0.5, least: 2, inset: 0.25, field: 20, street: 2 } as const;

export interface ClaimGround {
  /** The walled city: no ground outside it is claimed. */
  readonly enclosure: Ring;
  readonly buildable: readonly PolygonWithHoles[];
  /** Ground no building may take: the fortifications and the landmarks. */
  readonly reserved: readonly PolygonWithHoles[];
  /** The churches', convents' and gardens' ground: the free ground touching it is theirs and stays open. */
  readonly open: readonly PolygonWithHoles[];
}

/** Ground a house, its plot or a building on it takes: `owner` is the house's number, -1 for a gang. */
export interface Taken {
  readonly rings: readonly Ring[];
  readonly owner: number;
}

export interface Claims {
  readonly grid: ClaimGrid;
  /** Per cell, the house whose claim it is; -1 for ground no one claims, taken ground among it. */
  readonly owner: Int32Array;
  /** The cells claimed, in the order they were given out. */
  readonly cells: Int32Array;
}

const OFF = 0;
const WALLED = 1;
const FREE = 2;
const KEEP = 3;
const TAKEN = 4;
const LEFT_OPEN = 5;
const CLAIMED = 6;

const ringsOf = (p: PolygonWithHoles): readonly Ring[] => [p.outer, ...p.holes];

/* The walled buildable ground free, the reservations off it, and the churches' and gardens' ground kept. */
function groundState(grid: ClaimGrid, ground: ClaimGround): Uint8Array {
  const state = new Uint8Array(grid.cols * grid.rows);
  eachRunIn(grid, [ground.enclosure], (a, b) => state.fill(WALLED, a, b + 1));
  for (const p of ground.buildable)
    eachRunIn(grid, ringsOf(p), (a, b) => {
      for (let k = a; k <= b; k++) if (state[k] === WALLED) state[k] = FREE;
    });
  for (const p of ground.reserved) eachRunIn(grid, ringsOf(p), (a, b) => state.fill(OFF, a, b + 1));
  for (const p of ground.open) eachRunIn(grid, ringsOf(p), (a, b) => state.fill(KEEP, a, b + 1));
  return state;
}

/* Each piece of taken ground goes to the first that takes it. */
function take(
  grid: ClaimGrid,
  state: Uint8Array,
  owner: Int32Array,
  taken: readonly Taken[]
): void {
  for (const t of taken)
    eachRunIn(grid, t.rings, (a, b) => {
      for (let k = a; k <= b; k++) {
        if (state[k] !== FREE) continue;
        state[k] = TAKEN;
        owner[k] = t.owner;
      }
    });
}

/* The cells beside a cell on the grid, into `out`; how many there are. */
function neighbours(grid: ClaimGrid, k: number, out: Int32Array): number {
  const col = k % grid.cols;
  let n = 0;
  if (col > 0) out[n++] = k - 1;
  if (col < grid.cols - 1) out[n++] = k + 1;
  if (k >= grid.cols) out[n++] = k - grid.cols;
  if (k + grid.cols < grid.cols * grid.rows) out[n++] = k + grid.cols;
  return n;
}

/* Distance at least `half` from non-free ground means a (2*half-1)-wide square is free.
   Horizontal free runs, then vertical runs of those full-width rows, find the same centres in one
   scan with a column-sized buffer. Reversing restores the original backwards grid seed order. */
function fieldCells(grid: ClaimGrid, state: Uint8Array): Int32Array {
  const { cols, rows } = grid;
  const half = Math.ceil(CLAIM.field / CLAIM.cell / 2);
  const radius = half - 1;
  const width = radius * 2 + 1;
  const vertical = new Uint32Array(cols);
  const found: number[] = [];
  for (let row = 0; row < rows; row++) {
    const from = row * cols;
    let horizontal = 0;
    for (let col = 0; col < cols; col++) {
      horizontal = state[from + col] === FREE ? horizontal + 1 : 0;
      if (col < width - 1) continue;
      const centre = col - radius;
      vertical[centre] = horizontal >= width ? vertical[centre]! + 1 : 0;
      if (vertical[centre] >= width) found.push((row - radius) * cols + centre);
    }
  }
  return Int32Array.from(found.reverse());
}

/*
 * The free cells within `street` of the walled ground off the blocks, the streets, squares and water,
 * found step by step through the free cells from those beside it; they stay open.
 */
function openStreetEdge(grid: ClaimGrid, state: Uint8Array): void {
  let front = seedsBesideFree(grid, state, (k) => state[k] === WALLED);
  const near = new Int32Array(4);
  for (let step = 0; step < CLAIM.street / CLAIM.cell && front.length > 0; step++) {
    const next: number[] = [];
    for (const k of front) {
      const count = neighbours(grid, k, near);
      for (let i = 0; i < count; i++) {
        const n = near[i]!;
        if (state[n] !== FREE) continue;
        state[n] = LEFT_OPEN;
        next.push(n);
      }
    }
    front = Int32Array.from(next);
  }
}

/* The cells beside the free cells that `seed` names, in grid order, found from the free cells. */
function seedsBesideFree(
  grid: ClaimGrid,
  state: Uint8Array,
  seed: (k: number) => boolean
): Int32Array {
  const marked = new Uint8Array(state.length);
  const near = new Int32Array(4);
  for (let k = 0; k < state.length; k++) {
    if (state[k] !== FREE) continue;
    const count = neighbours(grid, k, near);
    for (let i = 0; i < count; i++) {
      const n = near[i]!;
      if (state[n] !== FREE && state[n] !== OFF && !marked[n] && seed(n)) marked[n] = 1;
    }
  }
  const found: number[] = [];
  for (let k = 0; k < marked.length; k++) if (marked[k]) found.push(k);
  return Int32Array.from(found);
}

/*
 * From the seeds, every free cell reachable through free cells, each once, in the order reached;
 * each is set to `to` and handed the owner of the cell it was reached from. The cells, in that order,
 * are the part of `queue`, a buffer as long as the grid, after the seeds.
 */
function spread(
  grid: ClaimGrid,
  state: Uint8Array,
  seeds: Int32Array,
  to: number,
  owner: Int32Array,
  queue: Int32Array
): Int32Array {
  queue.set(seeds);
  let tail = seeds.length;
  const near = new Int32Array(4);
  for (let head = 0; head < tail; head++) {
    const k = queue[head]!;
    const count = neighbours(grid, k, near);
    for (let i = 0; i < count; i++) {
      const n = near[i]!;
      if (state[n] !== FREE) continue;
      state[n] = to;
      owner[n] = owner[k]!;
      queue[tail++] = n;
    }
  }
  return queue.subarray(seeds.length, tail);
}

/** The free walled ground no house, plot or building takes, each cell given to the nearest house. */
export function claimLeftovers(ground: ClaimGround, taken: readonly Taken[]): Claims {
  const grid = gridOver(ringBounds(ground.enclosure), CLAIM.cell);
  const state = groundState(grid, ground);
  const takenBy = new Int32Array(state.length).fill(-1);
  take(grid, state, takenBy, taken);
  const queue = new Int32Array(state.length);
  const fields = fieldCells(grid, state);
  for (const k of fields) state[k] = LEFT_OPEN;
  spread(grid, state, fields, LEFT_OPEN, takenBy, queue);
  spread(
    grid,
    state,
    seedsBesideFree(grid, state, (k) => state[k] === KEEP),
    LEFT_OPEN,
    takenBy,
    queue
  );
  openStreetEdge(grid, state);
  const seeds = seedsBesideFree(grid, state, (k) => state[k] === TAKEN && takenBy[k]! >= 0);
  const cells = spread(grid, state, seeds, CLAIMED, takenBy, queue).slice();
  const owner = new Int32Array(state.length).fill(-1);
  for (const k of cells) owner[k] = takenBy[k]!;
  return { grid, owner, cells };
}
